// The type only, and from the module rather than the `@features/Speckle` barrel: that barrel
// re-exports a component and the Apollo-backed sync composable, and nothing importing those can be
// loaded under Node. This module stays pure so it can be unit-tested, which is also why the Speckle
// delete arrives as an argument rather than an import.
import type { DeleteEmptyModelOutcome } from "@/features/Speckle/speckleStreamModels";

import type { ModelType } from "../types/ModelType";

/**
 * The models staged for removal on the settings page, and what the page's Save does about them
 * (RTLCA-5756).
 *
 * Removing a model used to be the one control on that page that wrote at once, because the empty
 * Speckle model goes with the row and only the reader's own token may ask for that. Deferring it left
 * the Speckle call needing somewhere to live, and this is it: the Save deletes the models on the
 * stream, and the ROWS go by omission — `replaceProjectModels` re-inserts the whole set from
 * `modelSignature`, which leaves a staged row out.
 *
 * The order matters and is the reason this runs BEFORE the row write rather than after. A refusal
 * has to be able to keep the model, and it can only do that while the row write has not happened:
 * asking Speckle afterwards would mean discovering that a model holds geometry once it has already
 * been dropped from the project — and the sync would then re-import it as an INACTIVE row, quietly
 * out of the LCA. Refusing first costs nothing by comparison: if the stream delete lands and the row
 * write then throws, the row survives active and the sync's next pass recreates an empty model under
 * that name.
 *
 * A refusal clears the flag IN PLACE, which is the whole thread back to the write: `modelSignature`
 * is computed later, inside `updateBuildingProject`, so the model reappears in the set to re-insert
 * with nothing passed anywhere.
 */
export const pendingRemovals = <T extends Pick<ModelType, "pendingRemoval">>(models: T[]): T[] =>
  models.filter((model) => model.pendingRemoval);

export type RemovalRefusal = { name: string; reason: "hasVersions" | "failed" };

export const settlePendingModelRemovals = async (
  models: ModelType[],
  streamId: string,
  deleteEmptyModel: (streamId: string, modelName: string) => Promise<DeleteEmptyModelOutcome>
): Promise<RemovalRefusal[]> => {
  const staged = pendingRemovals(models);
  // No stream: there is no Speckle model to delete, so the row omission IS the whole removal.
  if (staged.length === 0 || !streamId) return [];

  const refusals: RemovalRefusal[] = [];
  // Sequentially, not in parallel: each `deleteEmptySpeckleModel` re-reads the stream's whole model
  // list, and one at a time keeps that off the Speckle server in a burst and the warnings in the
  // order the reader staged them. Never more than a handful of models.
  for (const model of staged) {
    const outcome = await deleteEmptyModel(streamId, model.name);
    // "deleted", or "notFound": already gone from the stream, so the removal stands either way.
    if (outcome !== "hasVersions" && outcome !== "failed") continue;
    model.pendingRemoval = false;
    refusals.push({ name: model.name, reason: outcome });
  }
  return refusals;
};

const quoted = (names: string[]) => names.map((name) => `"${name}"`).join(", ");

/**
 * One message per reason, because the reader's next move differs: a model that gained a version is
 * settled and simply stays, a stream call that failed is worth trying again. Both say the model is
 * still in the project — its chip has just reappeared among the selected ones with no mark on it,
 * and that needs to read as the intended outcome rather than as the staging having been forgotten.
 */
export const removalRefusalMessages = (refusals: RemovalRefusal[]): string[] => {
  const messages: string[] = [];

  const published = refusals.filter((refusal) => refusal.reason === "hasVersions").map((refusal) => refusal.name);
  if (published.length === 1) {
    messages.push(`"${published[0]}" has published versions, so it was kept. It is still in this project.`);
  } else if (published.length > 1) {
    messages.push(
      `${published.length} models have published versions, so they were kept: ${quoted(
        published
      )}. They are still in this project.`
    );
  }

  const failed = refusals.filter((refusal) => refusal.reason === "failed").map((refusal) => refusal.name);
  if (failed.length === 1) {
    messages.push(`The model "${failed[0]}" could not be removed from the stream, so it was kept. Try again.`);
  } else if (failed.length > 1) {
    messages.push(
      `${failed.length} models could not be removed from the stream, so they were kept: ${quoted(failed)}. Try again.`
    );
  }

  return messages;
};
